/*
 * An update named with its versions, the same in the install dialogs and the task tray (Ryan, 2026-10-22: "we should
 * do better about showing what the updates are (at least what version they're coming from)"): "LiteSpeed Cache
 * 7.9.1 → 7.9.2", a security fix marked as the Updates page marks it, and once its run reported, one that didn't
 * install says so.
 */
import type { UpdateLine } from "../api/bulk";
import { StatusPill } from "../ui/Badge";

export type VersionedUpdate = { key: string; name: string; from: string | null; to: string | null; security: boolean; status?: UpdateLine["status"] };

/** A run item's updates, for UpdateVersions. A PHP release is just "PHP 8.3.35 → 8.3.36"; Node.js's "Node.js 22.21.0 → 22.22.0". */
export const fromLines = (list: UpdateLine[]): VersionedUpdate[] =>
  list.map((u) => ({ key: `${u.type}:${u.slug}`, name: u.type === "php" ? "PHP" : u.type === "node" ? "Node.js" : u.name, from: u.from, to: u.to, security: u.security, status: u.status }));

const NOT_INSTALLED: Record<string, string> = { rolled_back: "Rolled back", failed: "Didn't update", skipped: "Skipped" };

function One({ u }: { u: VersionedUpdate }) {
  const missed = u.status && u.status !== "updated" ? NOT_INSTALLED[u.status] : null;
  return (
    <>
      {u.name} <span className="font-mono whitespace-nowrap">{`${u.from ?? "?"} → ${u.to ?? "?"}`}</span>
      {u.security ? (
        <StatusPill tone="warn" className="ml-1.5 align-middle">
          Security fix
        </StatusPill>
      ) : null}
      {missed ? <span className="font-medium text-bad"> · {missed}</span> : null}
    </>
  );
}

/** An update as a row: its name (and marks) on the left, its versions on the right, as the task tray lists them. */
function Row({ u }: { u: VersionedUpdate }) {
  const missed = u.status && u.status !== "updated" ? NOT_INSTALLED[u.status] : null;
  return (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-body">{u.name}</span>
        {u.security ? <StatusPill tone="warn">Security fix</StatusPill> : null}
        {missed ? <StatusPill tone="bad">{missed}</StatusPill> : null}
      </span>
      <span className="shrink-0 font-mono whitespace-nowrap text-dim">{`${u.from ?? "?"} → ${u.to ?? "?"}`}</span>
    </>
  );
}

/** The updates, one to a line, up to `max` named and "and 3 more" after. `rows`: each a row, versions on the right. */
export function UpdateVersions({ updates, max, className, rows }: { updates: VersionedUpdate[]; max?: number; className?: string; rows?: string }) {
  const shown = max && updates.length > max ? updates.slice(0, max) : updates;
  const more = updates.length - shown.length;
  return (
    <ul className={className}>
      {shown.map((u) => (
        <li key={u.key} className={rows ? `flex items-center justify-between gap-3 ${rows}` : undefined}>
          {rows ? <Row u={u} /> : <One u={u} />}
        </li>
      ))}
      {more ? <li className={rows ? `text-dim ${rows}` : undefined}>and {more} more</li> : null}
    </ul>
  );
}
